// Meta Pixel (a "dataset" in Meta Events Manager). Does nothing unless REACT_APP_META_PIXEL_ID is set, so previews stay
// silent and the ID never lives in the code.
//
// It also does nothing on a developer's own machine (localhost, 127.0.0.1, *.localhost) even when the ID is set in the local
// .env: otherwise every test booking would be counted as a real checkout and skew what Meta optimises the ads on. To test the
// pixel locally on purpose (for example with Meta's Pixel Helper), set REACT_APP_META_PIXEL_ON_LOCAL=true.
const isLocalHost = () => typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1|\[::1\]|.+\.localhost)$/i.test(window.location.hostname);
const configuredId = (process.env.REACT_APP_META_PIXEL_ID || '').trim();
const PIXEL_ID = isLocalHost() && process.env.REACT_APP_META_PIXEL_ON_LOCAL !== 'true' ? '' : configuredId;
const PURCHASE_SEEN_KEY = 'metaPixelPurchases';

let started = false;

export const initMetaPixel = () => {
  if (started || !PIXEL_ID || typeof window === 'undefined') return;
  started = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  // Meta would otherwise auto-fire PageView on every route change, including private pages we exclude.
  window.fbq.disablePushState = true;
  window.fbq.allowDuplicatePageViews = true;
  window.fbq('init', PIXEL_ID);
};

const send = (method, name, params, options) => {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;
  try {
    window.fbq(method, name, params || {}, options);
  } catch (error) {
    // Tracking must never break the site.
  }
};

export const trackPageView = () => send('track', 'PageView');
export const trackViewContent = (params) => send('track', 'ViewContent', params);

// Who the visitor is, once we know (they signed in, or are already signed in): Meta hashes it in the browser and uses it to match the
// events that follow to a person, which lifts how many events Meta can attribute. Only the e-mail, name and phone number the customer
// gave us; nothing is sent unhashed.
export const setMatchingData = ({ email, firstName, lastName, mobile } = {}) => {
  if (!PIXEL_ID || typeof window === 'undefined' || !window.fbq) return;
  const data = {};
  const mail = String(email || '').trim().toLowerCase();
  if (mail) data.em = mail;
  const first = String(firstName || '').trim().toLowerCase();
  if (first) data.fn = first;
  const last = String(lastName || '').trim().toLowerCase();
  if (last) data.ln = last;
  const digits = String(mobile || '').replace(/\D/g, '');
  if (digits.length >= 10) data.ph = digits.length === 10 ? `91${digits}` : digits;
  if (!Object.keys(data).length) return;
  try {
    // Meta's documented way to give the pixel the customer's details after the page has loaded: initialise it again with them.
    window.fbq('init', PIXEL_ID, data);
  } catch (error) {
    // Tracking must never break the site.
  }
};

// InitiateCheckout is sent from here, once per order, at the moment the payment link has been created (not on every press of the
// button): the visitor is signed in by then, so Meta can match it, and a repeated press or a retry on the same order adds nothing.
const CHECKOUT_SEEN_KEY = 'metaPixelCheckouts';
export const trackInitiateCheckout = ({ orderId, value, contentName }) => {
  if (!PIXEL_ID) return;
  let seen = [];
  try {
    seen = JSON.parse(localStorage.getItem(CHECKOUT_SEEN_KEY) || '[]');
  } catch (error) {
    seen = [];
  }
  if (orderId && seen.includes(orderId)) return;
  send(
    'track',
    'InitiateCheckout',
    { value: Number(value) || 0, currency: 'INR', content_name: contentName, content_type: 'product' },
    orderId ? { eventID: `ic_${orderId}` } : undefined
  );
  if (orderId) {
    try {
      localStorage.setItem(CHECKOUT_SEEN_KEY, JSON.stringify([...seen, orderId].slice(-20)));
    } catch (error) {
      // ignore
    }
  }
};

// Fires at most once per order (the success page can be reloaded or revisited).
export const trackPurchase = ({ orderId, value, contentName }) => {
  if (!PIXEL_ID) return;
  let seen = [];
  try {
    seen = JSON.parse(localStorage.getItem(PURCHASE_SEEN_KEY) || '[]');
  } catch (error) {
    seen = [];
  }
  if (orderId && seen.includes(orderId)) return;
  send(
    'track',
    'Purchase',
    { value: Number(value) || 0, currency: 'INR', content_name: contentName, content_type: 'product' },
    orderId ? { eventID: orderId } : undefined
  );
  if (orderId) {
    try {
      localStorage.setItem(PURCHASE_SEEN_KEY, JSON.stringify([...seen, orderId].slice(-20)));
    } catch (error) {
      // ignore: worst case the event repeats on a reload
    }
  }
};
